"use client"

import { useMemo, useState } from "react";
import { Clipboard } from "lucide-react";

import { Input } from "@/components/ui/input";
import { copyToClipboard } from "@/lib/utils";
import { useTranslations } from "@/components/i18n/use-translations";

// Milliseconds between each epoch and the Unix epoch (1970-01-01T00:00:00Z)
const EPOCH_1601_MS = BigInt(11644473600000);
const EPOCH_2001_MS = BigInt(978307200000);
const MIN_MS = Date.UTC(1990, 0, 1);
const MAX_MS = Date.UTC(2100, 0, 1);

const FORMATS: { name: string; toMs: (value: bigint) => bigint }[] = [
    { name: "Unix (seconds)", toMs: (value) => value * BigInt(1000) },
    { name: "Unix (milliseconds)", toMs: (value) => value },
    { name: "Unix (microseconds)", toMs: (value) => value / BigInt(1000) },
    { name: "Unix (nanoseconds)", toMs: (value) => value / BigInt(1000000) },
    { name: "Windows FILETIME / LDAP (100ns since 1601)", toMs: (value) => value / BigInt(10000) - EPOCH_1601_MS },
    { name: "WebKit / Chrome (microseconds since 1601)", toMs: (value) => value / BigInt(1000) - EPOCH_1601_MS },
    { name: "Apple Cocoa / Mac absolute (seconds since 2001)", toMs: (value) => value * BigInt(1000) + EPOCH_2001_MS },
];

const interpret = (raw: string) => {
    const value = raw.trim();
    if (!value) {
        return [];
    }

    if (/^-?\d+$/.test(value)) {
        const numeric = BigInt(value);
        return FORMATS.flatMap((format) => {
            const ms = Number(format.toMs(numeric));
            return ms >= MIN_MS && ms <= MAX_MS ? [{ name: format.name, date: new Date(ms) }] : [];
        });
    }

    if (/^-?\d+\.\d+$/.test(value)) {
        const ms = parseFloat(value) * 1000;
        return ms >= MIN_MS && ms <= MAX_MS ? [{ name: "Unix (seconds, fractional)", date: new Date(ms) }] : [];
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? [] : [{ name: "Date string", date: new Date(parsed) }];
};

export function TimestampConverter() {
    const [value, setValue] = useState("");
    const { messages } = useTranslations();
    const t = messages.tools.timestampConverter;

    const results = useMemo(() => interpret(value), [value]);

    return (
        <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
            <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{t.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{t.title}</h1>
                    <p className="text-sm text-gray-400">{t.description}</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                    <Input
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        placeholder={t.placeholder}
                        aria-label={t.label}
                        spellCheck={false}
                        className="h-11 flex-1 border border-slate-800 bg-transparent font-mono text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                    />
                    <button
                        type="button"
                        onClick={() => setValue(String(Math.floor(Date.now() / 1000)))}
                        className="h-11 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-5 text-sm font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500"
                    >
                        {t.now}
                    </button>
                </div>
            </div>

            {value.trim() && !results.length && (
                <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100">{t.noMatch}</p>
            )}

            {results.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-slate-800/80 bg-slate-950/60">
                    <table className="w-full min-w-[560px] text-left text-sm">
                        <thead className="border-b border-slate-800 text-xs uppercase tracking-wider text-gray-400">
                            <tr>
                                <th className="px-4 py-3 font-medium">{t.format}</th>
                                <th className="px-4 py-3 font-medium">{t.utc}</th>
                                <th className="px-4 py-3 font-medium">{t.local}</th>
                                <th className="px-2 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((result) => (
                                <tr key={result.name} className="border-b border-slate-800/60 last:border-0">
                                    <td className="px-4 py-3 text-gray-300">{result.name}</td>
                                    <td className="px-4 py-3 font-mono text-cyan-100">{result.date.toISOString()}</td>
                                    <td className="px-4 py-3 text-gray-300">{result.date.toLocaleString()}</td>
                                    <td className="px-2 py-3">
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(result.date.toISOString())}
                                            className="inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                                            aria-label={`${t.copy} ${result.name}`}
                                        >
                                            <Clipboard className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
